import React, { memo, useEffect, useState } from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { ChevronLeft } from '@mui/icons-material';
import { TextSelection } from '@tiptap/pm/state';
import { summarizeFieldInstruction } from './fieldLabels';

/** Template AI field node types (inline chip and block card). */
const FIELD_TYPES = new Set(['templateField', 'templateFieldBlock']);

/**
 * Whether a node is suggested for deletion in the open review (block or inline).
 * @param {import('@tiptap/pm/model').Node} node - Node.
 * @returns {boolean} True when it will be gone if the suggestions are accepted.
 */
const isDeleted = (node) => Boolean(
  (node.attrs && node.attrs.diffBlock === 'delete')
  || (node.marks && node.marks.some((m) => m.type.name === 'diffDelete'))
);

/**
 * Collect h1-h3 headings with their positions (top level and inside sections/fields), plus
 * whether each heading is AI-generated (its text is only an AI field: shown as the field's
 * summary) and whether its section (up to the next heading) contains AI fields.
 * @param {import('@tiptap/pm/model').Node} doc - Document.
 * @returns {Array<{pos: number, level: number, text: string, aiGenerated: boolean, hasFields: boolean}>} Headings.
 */
export const collectHeadings = (doc) => {
  const headings = [];
  let current = null;
  const markSection = () => {
    if (current) current.hasFields = true;
  };

  const walk = (parent, contentStart, inTable) => {
    parent.forEach((node, offset) => {
      // A block suggested for deletion is not part of the outline while it is under review.
      if (isDeleted(node)) return;
      const pos = contentStart + offset;
      const name = node.type.name;
      if (FIELD_TYPES.has(name)) {
        markSection();
        return;
      }
      if (name === 'heading') {
        // Show the heading as it will read if the suggestions are accepted.
        let text = '';
        const fields = [];
        node.forEach((child) => {
          if (isDeleted(child)) return;
          if (child.isText) text += child.text;
          else if (FIELD_TYPES.has(child.type.name)) fields.push(child);
        });
        text = text.trim();
        if (!inTable && node.attrs.level <= 3) {
          const summary = !text && fields.length
            ? fields.map((f) => summarizeFieldInstruction(f.attrs.instruction)).filter(Boolean).join(', ')
            : '';
          current = {
            pos,
            level: node.attrs.level,
            text: text || summary || (fields.length ? 'AI-generated heading' : 'Untitled heading'),
            aiGenerated: !text && fields.length > 0,
            hasFields: false,
          };
          headings.push(current);
        } else if (fields.length) {
          markSection();
        }
        return;
      }
      if (node.isTextblock) {
        node.forEach((child) => {
          if (!isDeleted(child) && FIELD_TYPES.has(child.type.name)) markSection();
        });
        return;
      }
      if (node.isLeaf) return;
      walk(node, pos + 1, inTable || name === 'table');
    });
  };

  walk(doc, 0, false);
  return headings;
};

/**
 * Index of the heading that contains or precedes a position.
 * @param {Array<{pos: number}>} headings - Headings.
 * @param {number} pos - Selection position.
 * @returns {number} Index or -1.
 */
const activeIndexFor = (headings, pos) => {
  let active = -1;
  for (let i = 0; i < headings.length; i += 1) {
    if (headings[i].pos <= pos) active = i;
    else break;
  }
  return active;
};

/**
 * Collapsible document outline (h1-h3) with click-to-scroll and active heading tracking.
 * AI-generated headings show their field's summary in italics after a ✦, and sections that
 * contain AI fields carry a small ✦. Recomputes headings at most every 300ms after changes.
 * @param {{editor: import('@tiptap/core').Editor, onClose?: () => void, footer?: React.ReactNode}} props - Props.
 * @returns {JSX.Element}
 */
const DocumentOutline = ({ editor, onClose, footer }) => {
  const [headings, setHeadings] = useState(() => (editor ? collectHeadings(editor.state.doc) : []));
  const [active, setActive] = useState(-1);

  useEffect(() => {
    if (!editor) return undefined;
    let timer = null;
    let latest = collectHeadings(editor.state.doc);
    setHeadings(latest);
    // Compare document identity, not only docChanged: resolving a review restores the saved
    // state with view.updateState (no transaction) and may then dispatch a step-less one.
    let lastDoc = editor.state.doc;
    const onTransaction = ({ transaction }) => {
      if (transaction.docChanged || transaction.getMeta('quillContentLoaded') || editor.state.doc !== lastDoc) {
        lastDoc = editor.state.doc;
        clearTimeout(timer);
        timer = setTimeout(() => {
          if (editor.isDestroyed) return;
          latest = collectHeadings(editor.state.doc);
          setHeadings(latest);
          setActive(activeIndexFor(latest, editor.state.selection.from));
        }, 300);
      }
    };
    const onSelection = () => setActive(activeIndexFor(latest, editor.state.selection.from));
    editor.on('transaction', onTransaction);
    editor.on('selectionUpdate', onSelection);
    return () => {
      clearTimeout(timer);
      editor.off('transaction', onTransaction);
      editor.off('selectionUpdate', onSelection);
    };
  }, [editor]);

  /**
   * Move the cursor to a heading and scroll it into view.
   * @param {{pos: number}} heading - Heading.
   * @returns {void}
   */
  const goTo = (heading) => {
    if (!editor || editor.isDestroyed) return;
    const { state, view } = editor;
    if (heading.pos > state.doc.content.size) return;
    const tr = state.tr.setSelection(TextSelection.near(state.doc.resolve(Math.min(heading.pos + 1, state.doc.content.size))));
    tr.setMeta('addToHistory', false);
    view.dispatch(tr);
    const dom = view.nodeDOM(heading.pos);
    if (dom && dom.scrollIntoView) dom.scrollIntoView({ behavior: 'smooth', block: 'start' });
    if (editor.isEditable) view.focus();
  };

  return (
    <Box
      component="nav"
      aria-label="Document outline"
      sx={{ display: 'flex', flexDirection: 'column', gap: '2px', py: 1 }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', px: 1.5, pb: 1 }}>
        <Typography sx={{ flex: 1, fontSize: 11.5, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'text.secondary' }}>
          Outline
        </Typography>
        {onClose && (
          <Tooltip title="Hide outline">
            <IconButton size="small" aria-label="Hide outline" onClick={onClose}>
              <ChevronLeft fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      {headings.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ px: 1.5, fontSize: 13 }}>
          Headings you add appear here.
        </Typography>
      )}
      {headings.map((heading, index) => {
        const isActive = index === active;
        const description = [
          heading.aiGenerated ? 'AI-generated heading' : null,
          heading.hasFields ? 'section has AI fields' : null,
        ].filter(Boolean).join(', ');
        return (
          <Box
            key={`${heading.pos}-${index}`}
            component="button"
            type="button"
            onClick={() => goTo(heading)}
            aria-current={isActive ? 'location' : undefined}
            aria-label={description ? `${heading.text} (${description})` : undefined}
            title={heading.aiGenerated ? `AI-generated: ${heading.text}` : heading.text}
            data-ai-heading={heading.aiGenerated ? 'true' : undefined}
            data-has-fields={heading.hasFields ? 'true' : undefined}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              width: '100%',
              textAlign: 'left',
              border: 0,
              cursor: 'pointer',
              minHeight: 30,
              py: 0.5,
              pr: 1,
              pl: 1.5 + (heading.level - 1) * 1.5,
              borderRadius: '8px',
              fontFamily: 'inherit',
              fontSize: 13.5,
              fontWeight: heading.level === 1 ? 600 : isActive ? 500 : 400,
              color: 'text.primary',
              // Active heading: neutral fill plus a 3px bar in the high-contrast ring colour.
              bgcolor: isActive ? 'var(--q-control-selected, #DCD8D0)' : 'transparent',
              boxShadow: isActive ? 'inset 3px 0 0 var(--q-control-selected-ring, #7A746B)' : 'none',
              '&:hover': { bgcolor: isActive ? 'var(--q-control-selected, #DCD8D0)' : 'action.hover' },
              '&:focus-visible': { outline: '2px solid var(--q-focus-ring, #4F3BD6)', outlineOffset: -2 },
            }}
          >
            {heading.aiGenerated && (
              <Box component="span" aria-hidden="true" sx={{ flexShrink: 0, color: 'var(--q-ai-text, #3A28B8)', fontSize: 12 }}>✦</Box>
            )}
            <Box
              component="span"
              sx={{
                flex: 1,
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontStyle: heading.aiGenerated ? 'italic' : 'normal',
                color: heading.aiGenerated ? 'text.secondary' : 'inherit',
              }}
            >
              {heading.text}
            </Box>
            {heading.hasFields && (
              <Box
                component="span"
                aria-hidden="true"
                data-testid="outline-field-marker"
                sx={{ flexShrink: 0, color: 'var(--q-ai-text, #3A28B8)', fontSize: 10.5, opacity: 0.8 }}
              >
                ✦
              </Box>
            )}
          </Box>
        );
      })}
      {footer && (
        <Box sx={{ mt: 2, mx: 1.5, pt: 1.5, borderTop: '1px solid', borderColor: 'divider', fontSize: 12.5, color: 'text.secondary' }}>
          {footer}
        </Box>
      )}
    </Box>
  );
};

export default memo(DocumentOutline);
